import { Link } from "react-router-dom";
import logoMark from "@/assets/nur-logo.png";

const LEGAL_LINKS = [
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms & Conditions" },
  { to: "/cookie-policy", label: "Cookie Policy" },
];

const H2 = ({ children }) => (
  <h2 className="pt-6 font-serif text-2xl tracking-tight text-forest md:text-3xl">
    {children}
  </h2>
);

const LegalLayout = ({ title, updated, testid, children }) => (
  <div className="relative min-h-screen bg-white" data-testid={testid}>
    <div className="grain-overlay" aria-hidden="true" />
    <header className="sticky top-0 z-40 border-b border-sage/30 bg-white/85 backdrop-blur-xl">
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-12">
        <Link to="/" data-testid="legal-logo" className="flex items-center">
          <img src={logoMark} alt="Nur Therapy" className="h-11 w-auto md:h-12" />
        </Link>
        <Link
          to="/"
          data-testid="legal-back-home"
          className="text-sm tracking-wide text-stone transition-colors hover:text-forest"
        >
          ← Back to home
        </Link>
      </nav>
    </header>

    <main className="mx-auto max-w-[820px] px-6 py-20 md:px-12 md:py-28">
      <p className="mb-4 text-[0.7rem] uppercase tracking-[0.3em] text-sage">Legal</p>
      <h1 className="font-serif text-4xl tracking-tight text-ink sm:text-5xl md:text-6xl">
        {title}
      </h1>
      {updated && <p className="mt-4 text-sm text-sage">Last updated: {updated}</p>}
      <div className="mt-8 space-y-5 text-base font-light leading-relaxed text-stone">
        {children}
      </div>
    </main>

    <footer className="border-t border-sage/30 bg-white px-6 py-12 md:px-12">
      <div className="mx-auto flex max-w-[1400px] flex-col gap-4 text-xs text-sage md:flex-row md:items-center md:justify-between">
        <p>© {new Date().getFullYear()} Hilal Organics &amp; Nur Therapies Ltd · Est. 2010. All rights reserved.</p>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {LEGAL_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="transition-colors hover:text-forest">
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </footer>
  </div>
);

export const PrivacyPolicy = () => (
  <LegalLayout title="Privacy Policy" updated="June 2026" testid="privacy-page">
    <p>
      Nur Therapy is a trading name of Hilal Organics &amp; Nur Therapies Ltd ("we",
      "us", "our"). We are committed to protecting and respecting your privacy. This
      policy explains how we collect, use and safeguard your personal information when you
      contact us or use our website, nurtherapy.co.uk.
    </p>

    <H2>Information we collect</H2>
    <p>
      When you make an enquiry via WhatsApp, email or our website, we may collect your
      name, contact details (phone number and/or email address) and any information you
      choose to share about your health or the treatment you're interested in.
    </p>

    <H2>How we use your information</H2>
    <ul className="list-disc space-y-2 pl-6">
      <li>To respond to your enquiries and messages.</li>
      <li>To arrange, confirm and manage your appointments.</li>
      <li>To provide safe and appropriate treatment tailored to you.</li>
      <li>To keep records as required for our professional practice.</li>
    </ul>

    <H2>Lawful basis</H2>
    <p>
      We process your data on the basis of your consent and our legitimate interest in
      responding to enquiries and providing our services. Where you share health
      information, we rely on your explicit consent.
    </p>

    <H2>Sharing your information</H2>
    <p>
      We never sell your data. We will not share your personal information with third
      parties except where required by law or where necessary to provide our services.
    </p>

    <H2>Data retention</H2>
    <p>
      We keep your information only for as long as necessary to provide our services and
      to meet our legal and professional obligations.
    </p>

    <H2>Your rights</H2>
    <p>
      Under UK GDPR you have the right to access, correct or request deletion of your
      personal data, and to withdraw consent at any time. To exercise these rights,
      please contact us at Info@nurtherapy.co.uk.
    </p>

    <H2>Cookies</H2>
    <p>
      Our website uses a limited number of cookies. Please see our Cookie Policy for full
      details.
    </p>

    <H2>Company information</H2>
    <p>
      This website and our services are operated by Hilal Organics &amp; Nur Therapies
      Ltd, trading as Nur Therapy, a company registered in England &amp; Wales (company
      number 12181496). Registered office: 451 High Road, Woodford Green, IG8 0XE, United
      Kingdom.
    </p>

    <H2>Contact us</H2>
    <p>Email: Info@nurtherapy.co.uk · Tel: 07984 341251.</p>
  </LegalLayout>
);

export const TermsConditions = () => (
  <LegalLayout title="Terms & Conditions" updated="June 2026" testid="terms-page">
    <p>
      These terms govern your use of the Nur Therapy website and services, operated by
      Hilal Organics &amp; Nur Therapies Ltd ("we", "us", "our"). By making an enquiry or
      booking a treatment with us, you agree to these terms.
    </p>

    <H2>Our services</H2>
    <p>
      Nur Therapy provides holistic and complementary therapies including TCM &
      Acupuncture, Hijama cupping, Ayurvedic massage and Phytobiophysics. Our therapies
      are complementary and are not a substitute for medical advice, diagnosis or
      treatment from a qualified doctor.
    </p>

    <H2>Health & safety</H2>
    <p>
      Please inform us of any medical conditions, medications or allergies, or if you are
      pregnant, before your treatment. If you have a medical concern, please consult your
      GP. Please note that Farid Ameen treats male clients only, and Nuriyah Bansi treats
      female clients only.
    </p>

    <H2>Bookings, cancellations & lateness</H2>
    <p>
      Appointments are by arrangement. We kindly ask for reasonable notice if you need to
      cancel or reschedule. Late arrivals may result in a shortened session.
    </p>

    <H2>Pricing & payment</H2>
    <p>
      Prices are listed on our website and may change without notice. Full details will
      be confirmed at the time of booking.
    </p>

    <H2>Liability</H2>
    <p>
      To the fullest extent permitted by law, Nur Therapy shall not be liable for any
      indirect or consequential loss. Nothing in these terms limits liability for death
      or personal injury caused by negligence.
    </p>

    <H2>Intellectual property</H2>
    <p>
      All content on this website, including the Nur Therapy name and logo, is our
      property and may not be used without our permission.
    </p>

    <H2>Governing law</H2>
    <p>These terms are governed by the laws of England and Wales.</p>

    <H2>Company information &amp; contact</H2>
    <p>
      Hilal Organics &amp; Nur Therapies Ltd, trading as Nur Therapy · registered in
      England &amp; Wales (company number 12181496) · Registered office: 451 High Road,
      Woodford Green, IG8 0XE, United Kingdom.
      <br />
      Email: Info@nurtherapy.co.uk · Tel: 07984 341251.
    </p>
  </LegalLayout>
);

export const CookiePolicy = () => (
  <LegalLayout title="Cookie Policy" updated="June 2026" testid="cookie-page">
    <p>
      This Cookie Policy explains how Nur Therapy uses cookies and similar technologies on
      nurtherapy.co.uk.
    </p>

    <H2>What are cookies?</H2>
    <p>
      Cookies are small text files stored on your device that help websites function and
      help us understand how the site is being used.
    </p>

    <H2>Cookies we use</H2>
    <ul className="list-disc space-y-2 pl-6">
      <li>
        <strong className="font-medium text-ink">Essential cookies</strong> — necessary
        for the website to function correctly.
      </li>
      <li>
        <strong className="font-medium text-ink">Analytics cookies</strong> — help us
        understand how visitors use the site so we can improve it.
      </li>
    </ul>

    <H2>Managing cookies</H2>
    <p>
      You can control or delete cookies through your browser settings. Please note that
      disabling some cookies may affect how the website works.
    </p>

    <H2>Contact us</H2>
    <p>
      Hilal Organics &amp; Nur Therapies Ltd (trading as Nur Therapy)
      <br />
      Email: Info@nurtherapy.co.uk · Tel: 07984 341251.
    </p>
  </LegalLayout>
);
